import * as React from "react";
import {
  ButtonToolbar,
  Button,
  OverlayTrigger,
  Form,
  DropdownButton,
  Dropdown,
  Card,
  Alert,
} from "react-bootstrap";
import ReactDatetime from "react-datetime";
import moment from "moment";
type Moment = moment.Moment;
import "moment/dist/locale/ja";
import { Loader } from "lib/components/Loader";
import I18n from "lib/I18n";
import * as Routes from "lib/routes";
import tooltips from "../../lib/tooltips";
import style from "./style.module.css";
import { envColors, timeZones } from "../../constants";
import { BsDownload, BsSearch } from "react-icons/bs";
import { getStore } from "../../store";
import { observer } from "mobx-react-lite";
import { Table } from "react-bootstrap";

const TIME_FORMAT = "YYYY-MM-DD HH:00";
function clampMoment(time: string | Moment): Moment {
  return (typeof time === "string" ? moment(time) : time).set({
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}
function formatMoment(time: string | Moment): string {
  return clampMoment(time).format(TIME_FORMAT);
}
function isValidTime(time?: string | Moment): boolean {
  return moment(time, TIME_FORMAT, true).isValid();
}

export const S3 = observer(() => {
  const { app, env, s3, jobStatus } = getStore();

  const onChangeTimeFrom = (
    time: React.FocusEvent<unknown> | string | Moment,
  ) => {
    if (typeof time === "string" || moment.isMoment(time)) {
      s3.timeFrom = formatMoment(time);
    }
  };

  const onChangeTimeTo = (
    time: React.FocusEvent<unknown> | string | Moment,
  ) => {
    if (typeof time === "string" || moment.isMoment(time)) {
      s3.timeTo = formatMoment(time);
    }
  };

  const onGetS3StatsClick = () => {
    s3.getS3Stats();
  };

  const onCopyFromS3Click = async () => {
    if (env.isLp1()) {
      if (!confirm(I18n.t("event_master_editor.messages.confirm_lp1_action"))) {
        return false;
      }
    }
    await s3.copyFromS3();
    jobStatus.getCopyStatus();
  };

  const onClickReload = () => {
    jobStatus.getCopyStatus();
  };

  const onChangeEnv = (value: string | null) => {
    if (value == null) {
      return;
    }
    env.selected = value;
  };

  const onSelectTimeZone = (value: string | null) => {
    if (value == null) {
      return;
    }
    s3.timeZone = value as "JST" | "UTC";
  };

  const envColor = (value: string) => {
    return envColors[value] || "primary";
  };

  const timeZoneDescription = (timeZone: string) => {
    return timeZones[timeZone];
  };

  const isValidTimeFrom = () => {
    return isValidTime(s3.timeFrom);
  };

  const isValidTimeTo = () => {
    return isValidTime(s3.timeTo);
  };

  const isValidDates = () => {
    return isValidTimeFrom() && isValidTimeTo();
  };

  const renderEnvItems = () => {
    return env.list.map((value) => (
      <Dropdown.Item key={value} eventKey={value}>
        {value}
      </Dropdown.Item>
    ));
  };

  const renderTimeZones = () => {
    return Object.keys(timeZones).map((value) => (
      <Dropdown.Item key={value} eventKey={value}>
        {timeZoneDescription(value)}
      </Dropdown.Item>
    ));
  };

  const renderDateFilter = () => {
    const { timeFrom, timeTo } = s3;
    return (
      <Form>
        <ButtonToolbar>
          <Dropdown className="me-2" onSelect={onChangeEnv}>
            <Dropdown.Toggle
              id="envdropdown"
              variant={envColor(env.selected)}
              title={env.selected}
              key={env.selected}
            >
              {env.selected}
            </Dropdown.Toggle>
            <Dropdown.Menu>{renderEnvItems()}</Dropdown.Menu>
          </Dropdown>
          <OverlayTrigger placement="top" overlay={tooltips.time_zone}>
            <DropdownButton
              id="tzdropdown"
              className="me-2"
              title={s3.timeZone}
              key={s3.timeZone}
              onSelect={onSelectTimeZone}
            >
              {renderTimeZones()}
            </DropdownButton>
          </OverlayTrigger>
          <Form.Group className="me-2">
            <OverlayTrigger placement="top" overlay={tooltips.time_from}>
              <ReactDatetime
                locale={I18n.locale}
                inputProps={{ placeholder: "YYYY-MM-DD HH:00" }}
                initialValue={timeFrom ? moment(timeFrom).toDate() : undefined}
                onClose={onChangeTimeFrom}
                dateFormat="YYYY-MM-DD"
                timeFormat="HH:00"
                strictParsing
              />
            </OverlayTrigger>
          </Form.Group>
          <Form.Group className="me-2">
            <OverlayTrigger placement="top" overlay={tooltips.time_to}>
              <ReactDatetime
                locale={I18n.locale}
                inputProps={{ placeholder: "YYYY-MM-DD HH:00" }}
                initialValue={timeTo ? moment(timeTo).toDate() : undefined}
                onClose={onChangeTimeTo}
                dateFormat="YYYY-MM-DD"
                timeFormat="HH:00"
                strictParsing
              />
            </OverlayTrigger>
          </Form.Group>
          <OverlayTrigger
            delay={200}
            placement="top"
            overlay={tooltips.s3_stats}
          >
            <Button
              className="search-button me-2"
              onClick={onGetS3StatsClick}
              disabled={!isValidDates()}
            >
              <BsSearch />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            delay={200}
            placement="top"
            overlay={tooltips.copy_from_s3}
          >
            <Button
              className="copy-button me-2"
              onClick={onCopyFromS3Click}
              disabled={!isValidDates()}
            >
              <BsDownload />
            </Button>
          </OverlayTrigger>
        </ButtonToolbar>
        <ul className="text-danger">
          {isValidDates() || (
            <li>{I18n.t("event_master_editor.errors.invalid_date")}</li>
          )}
        </ul>
      </Form>
    );
  };

  const renderTable = () => {
    return (
      <Table striped hover size="sm">
        <thead>
          <tr>
            <td>{I18n.t("event_master_editor.label.datetime")}</td>
            <td>{I18n.t("event_master_editor.label.file_count")}</td>
            <td>{I18n.t("event_master_editor.label.total_size")}</td>
          </tr>
        </thead>
        <tbody>
          {s3.s3StatsList.map((item) => (
            <tr key={item.datetime}>
              <td>{item.datetime}</td>
              <td>{item.fileCount}</td>
              <td>{item.totalSize}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const linkToJob = () => {
    if (jobStatus.copy.jobId != null) {
      const path = Routes.title_job_path(app.titleUuid, jobStatus.copy.jobId, {
        event_master_id: app.eventMasterId,
      });
      const message = I18n.t("event_master_editor.label.link_to_copy_job");
      return <a href={path}>{message}</a>;
    }
    return null;
  };

  const renderJobStatus = () => {
    return (
      <Loader show={jobStatus.copy.isLoading}>
        <Card>
          <Card.Header>
            {I18n.t("event_master_editor.label.copy_job_status")}
          </Card.Header>
          <Card.Body>
            <p>{jobStatus.copy.value}</p>
            <p>{linkToJob()}</p>
            <ButtonToolbar>
              <Button onClick={onClickReload}>
                {I18n.t("event_master_editor.action.reload")}
              </Button>
            </ButtonToolbar>
          </Card.Body>
        </Card>
      </Loader>
    );
  };

  return (
    <div className={style.root}>
      <Alert variant="info">
        {I18n.t("event_master_editor.messages.s3_admin_desc")}
      </Alert>
      <Card>
        <Card.Header>
          {I18n.t("event_master_editor.messages.s3_desc")}
        </Card.Header>
        <Card.Body>
          <div>{renderDateFilter()}</div>
          <Loader show={s3.isLoading}>{renderTable()}</Loader>
        </Card.Body>
      </Card>
      {renderJobStatus()}
    </div>
  );
});
